import { computed, inject, Injectable, signal } from '@angular/core';

import { AbstractChatAnalyticsService } from '../analytics/abstract-chat-analytics.service';

import { type ChatActivityStatus } from './interfaces/chat.interfaces';

/**
 * State of a single chat window instance.
 */
export interface ChatWindowState {
  /** Unique window identifier */
  id: string;
  /** Display title (truncated first message or "New Chat") */
  title: string;
  /** Whether the window is open (visible) or minimized (tab only) */
  state: 'open' | 'minimized';
  /** Whether at least one message has been sent in this window */
  hasMessages: boolean;
  /** Activity hint shown on the minimized tab */
  status: ChatActivityStatus;
  /** If set, the window should load this session on init */
  initialSessionId?: string;
}

/**
 * Manages multiple floating chat windows.
 * Only one window can be in 'open' state at a time; others are minimized.
 */
@Injectable({ providedIn: 'root' })
export class ChatWindowService {
  private readonly _abstractChatAnalyticsService = inject(
    AbstractChatAnalyticsService,
    { optional: true },
  );

  private readonly _windows = signal<ChatWindowState[]>([]);
  private readonly _expanded = signal(false);
  private _nextId = 0;

  /** All chat windows (open + minimized) */
  public readonly windows = this._windows.asReadonly();

  /** The currently open (visible) window, if any */
  public readonly activeWindow = computed(() =>
    this._windows().find(w => w.state === 'open'),
  );

  /** Whether any window exists (controls footer bar visibility) */
  public readonly hasWindows = computed(() => this._windows().length > 0);

  /** Whether all windows are in expanded mode */
  public readonly expanded = this._expanded.asReadonly();

  /** Only windows with messages get a tab in the footer bar */
  public readonly tabbedWindows = computed(() =>
    this._windows().filter(w => w.hasMessages),
  );

  /**
   * Opens a new empty chat window.
   * If an empty window already exists, focuses it instead.
   */
  public openNewChat(): void {
    const windows = this._windows();
    const emptyWindow = windows.find(w => !w.hasMessages);

    if (emptyWindow) {
      this.focusWindow(emptyWindow.id);

      return;
    }

    this._abstractChatAnalyticsService?.trackWindowOpened({
      isNewChat: true,
      source: 'new_chat',
    });

    const id = `chat-window-${++this._nextId}`;
    const newWindow: ChatWindowState = {
      id,
      title: 'New Chat',
      state: 'open',
      hasMessages: false,
      status: 'idle',
    };

    this._windows.update(ws => [
      ...ws.map((w): ChatWindowState => ({ ...w, state: 'minimized' })),
      newWindow,
    ]);
  }

  /**
   * Opens a historical session in a new window.
   * If a window for that session already exists, focuses it instead.
   */
  public openSession(sessionId: string, title: string): void {
    const existing = this._windows().find(
      w => w.initialSessionId === sessionId,
    );

    if (existing) {
      this.focusWindow(existing.id);

      return;
    }

    this._abstractChatAnalyticsService?.trackWindowOpened({
      isNewChat: false,
      source: 'historical_session',
    });

    const id = `chat-window-${++this._nextId}`;
    const newWindow: ChatWindowState = {
      id,
      title,
      state: 'open',
      hasMessages: true,
      status: 'idle',
      initialSessionId: sessionId,
    };

    this._windows.update(ws => [
      ...ws.map((w): ChatWindowState => ({ ...w, state: 'minimized' })),
      newWindow,
    ]);
  }

  /**
   * Brings a minimized window to the foreground.
   * All other windows are minimized.
   */
  public focusWindow(id: string): void {
    const target = this._windows().find(w => w.id === id);

    if (target && target.state !== 'open') {
      this._abstractChatAnalyticsService?.trackWindowOpened({
        isNewChat: !target.hasMessages,
        source: 'window_focus',
      });
    }

    this._windows.update((windows): ChatWindowState[] =>
      windows.map(w =>
        w.id === id
          ? { ...w, state: 'open', status: 'idle' }
          : { ...w, state: 'minimized' },
      ),
    );
  }

  /**
   * Minimizes a window to the footer bar.
   * Empty windows (no messages) are removed instead since they have no tab.
   */
  public minimizeWindow(id: string): void {
    this._windows.update((windows): ChatWindowState[] => {
      const target = windows.find(w => w.id === id);

      if (target && !target.hasMessages) {
        return windows.filter(w => w.id !== id);
      }

      return windows.map(w => (w.id === id ? { ...w, state: 'minimized' } : w));
    });
  }

  /**
   * Closes (removes) a window entirely.
   */
  public closeWindow(id: string): void {
    this._windows.update(windows => windows.filter(w => w.id !== id));
  }

  /**
   * Toggles the expanded state (shared across all windows).
   */
  public toggleExpand(): void {
    this._expanded.update(v => !v);
  }

  /**
   * Updates the display title of a window and marks it as having messages.
   */
  public updateTitle(id: string, title: string): void {
    this._windows.update((windows): ChatWindowState[] =>
      windows.map(w => (w.id === id ? { ...w, title, hasMessages: true } : w)),
    );
  }

  /**
   * Updates the activity hint shown on a window's minimized tab.
   */
  public setStatus(id: string, status: ChatActivityStatus): void {
    this._windows.update((windows): ChatWindowState[] =>
      windows.map(w => (w.id === id ? { ...w, status } : w)),
    );
  }

  /**
   * Toggle behaviour for the header button:
   * - No windows exist -> open a new chat
   * - An active (open) window exists -> minimize it
   * - All windows minimized -> focus the last one
   */
  public toggleChat(): void {
    const windows = this._windows();

    if (!windows.length) {
      this.openNewChat();

      return;
    }

    const activeWindow = windows.find(w => w.state === 'open');

    if (activeWindow) {
      this.minimizeWindow(activeWindow.id);

      return;
    }

    this.focusWindow(windows[windows.length - 1].id);
  }
}
